import { useEffect, useMemo, useState } from 'react';
import {
  Shield, Plus, Edit2, Trash2, X, Check, ChevronDown, ChevronUp,
  KeyRound, Copy, Info,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { permissionRoleService } from '../../services/portalUserService';
import { PERMISSION_MODULES, ACTION_LABELS } from '../../utils/constants';
import { UI, ui, buttonStyle, fieldStyles } from '../../utils/ui';

// ─── Predefined role colors ───────────────────────────────────────────────────

const ROLE_COLORS = [
  '#7c3aed', '#2563eb', '#16a34a', '#ea580c', '#db2777',
  '#0891b2', '#65a30d', '#9333ea', '#d97706', '#475569',
];

const inputRowStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 14px', borderRadius: 14,
  border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff',
};
const inputCore = {
  border: 'none', outline: 'none', flex: 1, fontSize: 14,
  backgroundColor: 'transparent', color: UI.colors.text,
};

// ─── Permission Matrix Editor ─────────────────────────────────────────────────

const PermissionMatrix = ({ permissions, onChange }) => {
  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(Object.keys(PERMISSION_MODULES).map((k) => [k, true]))
  );

  const hasAction = (module, action) =>
    Array.isArray(permissions[module]) && permissions[module].includes(action);

  const toggleAction = (module, action) => {
    const current = permissions[module] || [];
    const updated = current.includes(action)
      ? current.filter((a) => a !== action)
      : [...current, action];
    onChange({ ...permissions, [module]: updated });
  };

  const toggleModule = (module) => {
    const allActions = PERMISSION_MODULES[module].actions;
    const current = permissions[module] || [];
    const allSelected = allActions.every((a) => current.includes(a));
    onChange({ ...permissions, [module]: allSelected ? [] : [...allActions] });
  };

  const moduleAllSelected = (module) => {
    const allActions = PERMISSION_MODULES[module].actions;
    const current = permissions[module] || [];
    return allActions.every((a) => current.includes(a));
  };

  const moduleSomeSelected = (module) => {
    const current = permissions[module] || [];
    return current.length > 0 && !moduleAllSelected(module);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Object.entries(PERMISSION_MODULES).map(([moduleKey, moduleDef]) => {
        const isExpanded = expanded[moduleKey];
        const allSel = moduleAllSelected(moduleKey);
        const someSel = moduleSomeSelected(moduleKey);
        const selectedCount = (permissions[moduleKey] || []).length;

        return (
          <div
            key={moduleKey}
            style={{
              border: `1.5px solid ${allSel ? UI.colors.primary : someSel ? '#fbbf24' : UI.colors.border}`,
              borderRadius: 12,
              overflow: 'hidden',
              transition: 'border-color 0.2s',
            }}
          >
            {/* Module Header */}
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px',
                backgroundColor: allSel ? '#f0fdf4' : someSel ? '#fffbeb' : '#f9fafb',
                cursor: 'pointer', userSelect: 'none',
              }}
              onClick={() => setExpanded((p) => ({ ...p, [moduleKey]: !p[moduleKey] }))}
            >
              {/* Select-all checkbox */}
              <div
                onClick={(e) => { e.stopPropagation(); toggleModule(moduleKey); }}
                style={{
                  width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                  border: `2px solid ${allSel ? UI.colors.primary : someSel ? '#fbbf24' : '#d1d5db'}`,
                  backgroundColor: allSel ? UI.colors.primary : someSel ? '#fffbeb' : '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                {allSel && <Check size={11} color="#fff" />}
                {someSel && <div style={{ width: 8, height: 2, backgroundColor: '#fbbf24', borderRadius: 1 }} />}
              </div>

              <span style={{ fontWeight: 700, fontSize: 13, color: UI.colors.text, flex: 1 }}>
                {moduleDef.label}
              </span>

              {selectedCount > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 900,
                  backgroundColor: allSel ? UI.colors.primary : '#fbbf24',
                  color: '#fff', padding: '2px 8px', borderRadius: 20,
                }}>
                  {selectedCount}/{moduleDef.actions.length}
                </span>
              )}

              {isExpanded ? <ChevronUp size={15} color={UI.colors.muted} /> : <ChevronDown size={15} color={UI.colors.muted} />}
            </div>

            {/* Actions Grid */}
            {isExpanded && (
              <div style={{
                padding: '12px 16px',
                display: 'flex', flexWrap: 'wrap', gap: 10,
                borderTop: `1px solid ${UI.colors.border}`,
                backgroundColor: '#ffffff',
              }}>
                {moduleDef.actions.map((action) => {
                  const checked = hasAction(moduleKey, action);
                  return (
                    <label
                      key={action}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7,
                        padding: '7px 12px', borderRadius: 9999,
                        border: `1.5px solid ${checked ? UI.colors.primary : UI.colors.border}`,
                        backgroundColor: checked ? '#f0fdf4' : '#ffffff',
                        cursor: 'pointer', userSelect: 'none', fontSize: 13, fontWeight: 600,
                        color: checked ? '#15803d' : UI.colors.muted,
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAction(moduleKey, action)}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: `2px solid ${checked ? UI.colors.primary : '#d1d5db'}`,
                        backgroundColor: checked ? UI.colors.primary : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {checked && <Check size={9} color="#fff" />}
                      </div>
                      {ACTION_LABELS[action] || action}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Create / Edit Role Modal ─────────────────────────────────────────────────

const RoleModal = ({ editingRole, onClose, onSaved }) => {
  const isEdit = Boolean(editingRole);

  const [form, setForm] = useState({
    name: editingRole?.name || '',
    description: editingRole?.description || '',
    color: editingRole?.color || '#7c3aed',
    permissions: editingRole?.permissions || {},
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const totalSelected = Object.values(form.permissions).reduce(
    (sum, actions) => sum + (Array.isArray(actions) ? actions.length : 0), 0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Role name is required.');
    if (totalSelected === 0) return toast.error('Select at least one permission.');

    setSaving(true);
    try {
      let result;
      if (isEdit) {
        result = await permissionRoleService.update(editingRole.id, form);
        toast.success(`Role "${result.data.role.name}" updated.`);
      } else {
        result = await permissionRoleService.create(form);
        toast.success(`Role "${result.data.role.name}" created!`);
      }
      onSaved(result.data.role, isEdit);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save role.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={onClose} />
      <div style={{
        ...ui.card, position: 'relative', width: '100%', maxWidth: 620, zIndex: 1,
        padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${UI.colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: form.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <KeyRound size={20} color={form.color} />
            </div>
            <div>
              <h2 style={{ fontFamily: UI.font.heading, fontSize: 22, fontWeight: 900, margin: 0, color: UI.colors.text }}>
                {isEdit ? 'Edit Role' : 'Create Permission Role'}
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: UI.colors.muted }}>
                {totalSelected} permission{totalSelected !== 1 ? 's' : ''} selected
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: UI.colors.muted, display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Name + Color */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={fieldStyles.label}>Role Name *</label>
              <div className="input-row" style={inputRowStyle}>
                <Shield size={16} color={form.name ? form.color : '#9ca3af'} />
                <input
                  type="text"
                  placeholder="e.g. Receptionist, Manager"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  style={inputCore}
                  required
                />
              </div>
            </div>
            <div>
              <label style={fieldStyles.label}>Color</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxWidth: 180 }}>
                {ROLE_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set('color', c)}
                    style={{
                      width: 26, height: 26, borderRadius: '50%', border: `3px solid ${form.color === c ? '#1f2937' : 'transparent'}`,
                      backgroundColor: c, cursor: 'pointer', flexShrink: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={fieldStyles.label}>Description <span style={{ color: UI.colors.muted, fontWeight: 400 }}>(optional)</span></label>
            <textarea
              placeholder="Brief description of this role's responsibilities..."
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              style={{ ...fieldStyles.input, resize: 'none', fontFamily: UI.font.body }}
            />
          </div>

          {/* Permission Matrix */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <label style={fieldStyles.label}>Permissions *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    const all = {};
                    Object.entries(PERMISSION_MODULES).forEach(([k, v]) => { all[k] = [...v.actions]; });
                    set('permissions', all);
                  }}
                  style={{ ...buttonStyle('outline'), padding: '5px 10px', borderRadius: 8, fontSize: 12 }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => set('permissions', {})}
                  style={{ ...buttonStyle('outline'), padding: '5px 10px', borderRadius: 8, fontSize: 12 }}
                >
                  Clear All
                </button>
              </div>
            </div>
            <PermissionMatrix
              permissions={form.permissions}
              onChange={(p) => set('permissions', p)}
            />
          </div>
        </form>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${UI.colors.border}`, display: 'flex', gap: 10, flexShrink: 0 }}>
          <button type="button" onClick={onClose} style={{ ...buttonStyle('outline'), flex: 1, padding: '12px 16px', borderRadius: 14 }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{ ...buttonStyle('primary'), flex: 1, padding: '12px 16px', borderRadius: 14, opacity: saving ? 0.75 : 1, backgroundColor: form.color, borderColor: form.color }}
          >
            {saving ? (
              <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            ) : (
              <Check size={16} />
            )}
            {saving ? 'Saving...' : isEdit ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Role Card ────────────────────────────────────────────────────────────────

const RoleCard = ({ role, onEdit, onDelete }) => {
  const totalPerms = Object.values(role.permissions || {}).reduce(
    (s, a) => s + (Array.isArray(a) ? a.length : 0), 0
  );
  const moduleCount = Object.values(role.permissions || {}).filter((a) => a.length > 0).length;

  return (
    <div style={{ ...ui.card, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: role.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Shield size={22} color={role.color} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: UI.colors.text }}>{role.name}</h3>
            {role.description && (
              <p style={{ margin: '3px 0 0', fontSize: 12, color: UI.colors.muted }}>{role.description}</p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={() => onEdit(role)} style={{ ...buttonStyle('outline'), padding: '7px 10px', borderRadius: 10 }} title="Edit role">
            <Edit2 size={14} />
          </button>
          <button onClick={() => onDelete(role)} style={{ ...buttonStyle('outline'), padding: '7px 10px', borderRadius: 10, borderColor: '#fecaca', color: UI.colors.danger }} title="Delete role">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ padding: '6px 12px', borderRadius: 8, backgroundColor: role.color + '15', fontSize: 12, fontWeight: 700, color: role.color }}>
          {totalPerms} permission{totalPerms !== 1 ? 's' : ''}
        </div>
        <div style={{ padding: '6px 12px', borderRadius: 8, backgroundColor: '#f3f4f6', fontSize: 12, fontWeight: 700, color: UI.colors.muted }}>
          {moduleCount} module{moduleCount !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Permission pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {Object.entries(role.permissions || {}).filter(([, actions]) => actions.length > 0).map(([module]) => (
          <span key={module} style={{
            fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 9999,
            border: `1px solid ${role.color}40`, color: role.color, backgroundColor: role.color + '10',
          }}>
            {PERMISSION_MODULES[module]?.label || module}
          </span>
        ))}
      </div>

      {/* Created */}
      {role.created_by_name && (
        <p style={{ margin: 0, fontSize: 11, color: UI.colors.muted }}>
          Created by {role.created_by_name} · {new Date(role.created_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ManagePermissionRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    permissionRoleService.getAll()
      .then((res) => setRoles(res.data.roles || []))
      .catch(() => toast.error('Failed to load permission roles.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSaved = (role, isEdit) => {
    setRoles((prev) =>
      isEdit ? prev.map((r) => r.id === role.id ? role : r) : [role, ...prev]
    );
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setShowModal(true);
  };

  const handleDelete = (role) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 8px', fontWeight: 900, color: UI.colors.text }}>Delete "{role.name}"?</p>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: UI.colors.muted }}>Staff assigned to this role will lose their permissions.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              setDeleting(role.id);
              try {
                await permissionRoleService.delete(role.id);
                setRoles((prev) => prev.filter((r) => r.id !== role.id));
                toast.success(`Role "${role.name}" deleted.`);
              } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to delete role.');
              } finally {
                setDeleting(null);
              }
            }}
            style={{ ...buttonStyle('danger'), padding: '8px 14px', borderRadius: 12 }}
          >
            Delete
          </button>
          <button onClick={() => toast.dismiss(t.id)} style={{ ...buttonStyle('outline'), padding: '8px 14px', borderRadius: 12 }}>
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {(showModal || editingRole) && (
        <RoleModal
          editingRole={editingRole}
          onClose={() => { setShowModal(false); setEditingRole(null); }}
          onSaved={handleSaved}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>
            Permission Roles
          </h1>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>
            Define named roles with module-level access controls for portal staff
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditingRole(null); setShowModal(true); }}
          style={{ ...buttonStyle('primary'), padding: '11px 20px', borderRadius: 14 }}
        >
          <Plus size={16} /> Create Role
        </button>
      </div>

      {/* Info banner */}
      <div style={{ padding: '12px 16px', borderRadius: 12, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <Info size={16} color="#2563eb" style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <p style={{ margin: 0, fontSize: 13, color: '#1d4ed8', fontWeight: 700 }}>How Permission Roles Work</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#3b82f6' }}>
            Create roles like "Receptionist" or "Manager" with specific module permissions, then assign them to portal users. Owners always have full access regardless of roles.
          </p>
        </div>
      </div>

      {/* Roles Grid */}
      {roles.length === 0 ? (
        <div style={{ ...ui.card, padding: '56px 18px', textAlign: 'center' }}>
          <KeyRound size={44} color="#d1d5db" style={{ margin: '0 auto 16px', display: 'block' }} />
          <p style={{ margin: 0, fontWeight: 900, color: UI.colors.text, fontSize: 16 }}>No permission roles yet</p>
          <p style={{ margin: '8px 0 16px', fontSize: 13, color: UI.colors.muted }}>Create your first role to start assigning portal users.</p>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            style={{ ...buttonStyle('primary'), padding: '10px 20px', borderRadius: 12 }}
          >
            <Plus size={15} /> Create First Role
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {roles.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ManagePermissionRoles;
